import { h } from 'preact';
import { useState } from 'preact/hooks';

/**
 * Render a field inside a collapsible item based on column type.
 */
function ItemField({ column, value, onChange, disabled }) {
  const type = column.type || 'text';
  const fieldId = `list-field-${column.key}`;

  const input = (() => {
    switch (type) {
      case 'select':
        return (
          <select
            id={fieldId}
            class="epx-entry-input"
            value={value || ''}
            disabled={disabled || column.disabled}
            onChange={(e) => onChange(e.target.value)}
          >
            {(column.options || []).map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <label class="epx-checkbox-label" htmlFor={fieldId}>
            <input
              id={fieldId}
              type="checkbox"
              class="epx-entry-checkbox"
              checked={!!value}
              disabled={disabled || column.disabled}
              onChange={(e) => onChange(e.target.checked)}
            />
            {column.label}
          </label>
        );

      case 'number':
        return (
          <input
            id={fieldId}
            type="number"
            class="epx-entry-input"
            value={value != null ? value : ''}
            min={column.min}
            max={column.max}
            step={column.step}
            disabled={disabled || column.disabled}
            onInput={(e) => onChange(e.target.value)}
          />
        );

      default:
        return (
          <input
            id={fieldId}
            type="text"
            class="epx-entry-input"
            value={value || ''}
            placeholder={column.placeholder || ''}
            disabled={disabled || column.disabled}
            onInput={(e) => onChange(e.target.value)}
          />
        );
    }
  })();

  return (
    <div class="epx-entry epx-list-item-field">
      {type !== 'checkbox' && (
        <label class="epx-entry-label" htmlFor={fieldId}>{column.label}</label>
      )}
      {input}
    </div>
  );
}

/**
 * A single collapsible list item (accordion pattern matching bpmn-io).
 */
function ListItem({ item, index, columns, onChange, onRemove }) {
  const [isOpen, setIsOpen] = useState(false);

  // Build a summary label from the first text/select column value
  const summaryCol = columns[0];
  const summaryValue = item[summaryCol.key];
  let summaryLabel = summaryValue || `Item ${index + 1}`;
  // For select columns, show the option label instead of the raw value
  if (summaryCol.type === 'select' && summaryCol.options) {
    const opt = summaryCol.options.find(o => o.value === summaryValue);
    if (opt) summaryLabel = opt.label;
  }

  return (
    <div class={`epx-list-item ${isOpen ? 'epx-list-item--open' : ''}`}>
      <div class="epx-list-item-header" onClick={() => setIsOpen(!isOpen)}>
        <span class="epx-list-item-arrow">{isOpen ? '\u25BE' : '\u25B8'}</span>
        <span class="epx-list-item-label">{summaryLabel}</span>
        <button
          class="epx-btn-remove"
          onClick={(e) => { e.stopPropagation(); onRemove(index); }}
          title="Remove"
        >&times;</button>
      </div>
      {isOpen && (
        <div class="epx-list-item-content">
          {columns.map(col => (
            <ItemField
              key={col.key}
              column={col}
              value={item[col.key]}
              onChange={(val) => onChange(index, col.key, val)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ListEntry({
  id,
  label,
  items = [],
  columns = [],
  onAdd,
  onRemove,
  onChange,
  addLabel,
  badge = true,
  autoOpen = true,
}) {
  return (
    <div class="epx-entry epx-list">
      {items.map((item, idx) => (
        <ListItem
          key={idx}
          item={item}
          index={idx}
          columns={columns}
          onChange={onChange}
          onRemove={onRemove}
        />
      ))}
      {onAdd && (
        <button class="epx-btn-add" onClick={onAdd}>{addLabel || '+ Add'}</button>
      )}
    </div>
  );
}
